import React, { useState, useCallback, useMemo } from 'react';
import { useRecorder } from '../../hooks/useRecorder';
import useRecordingTimer from '../../hooks/useRecordingTimer';
import startSession from '../../utils/session/startSession';
import updateSession from '../../utils/session/updateSession';

const RecordingContext = React.createContext();

export function RecordingProvider({ children }) {
  // Recording state and logic
  const recorder = useRecorder();
  const timer = useRecordingTimer();

  // UI state: modal vs bar
  const [showRecordingModal, setShowRecordingModal] = useState(false);
  // Derived: show bar if not modal and recording or paused
  const showRecordingBar = !showRecordingModal && (recorder.isRecording || recorder.isPaused);

  // Patient state
  const [patient, setPatient] = useState(null);
  const [fromPatientDetails, setFromPatientDetails] = useState(false);

  // Notes state
  const [userNotes, setUserNotes] = useState('');
  const resetUserNotes = useCallback(() => setUserNotes(''), []);

  // Session UUID state - can be set externally (e.g., for document uploads)
  const [preRecordingSessionUuid, setPreRecordingSessionUuid] = useState(null);

  // Attached documents state - persists across modal and bar
  const [attachedDocuments, setAttachedDocuments] = useState([]);
  const resetAttachedDocuments = useCallback(() => setAttachedDocuments([]), []);

  // Start recording
  const startRecording = useCallback(async () => {
    let sessionUuidToUse = preRecordingSessionUuid;
    // If we don't already have a pre-created session, create one including the selected patient
    if (!sessionUuidToUse && patient?.getUUID()) {
      try {
        sessionUuidToUse = await startSession({ patientUuid: patient.getUUID() });
      } catch (_) {
        // If session creation fails here, recorder.start will attempt to create without patient
      }
    }

    await recorder.start(sessionUuidToUse);
    // If we couldn't pre-create the session with patient, ensure we link it now on the server
    try {
      if (!sessionUuidToUse && patient?.getUUID && recorder.sessionUuid) {
        await updateSession(recorder.sessionUuid, { patient_uuid: patient.getUUID() });
      }
    } catch (_) {
      // Non-blocking; finalize will still attempt to pass patient_uuid
    }
    setShowRecordingModal(false);
    timer.start();
    setPreRecordingSessionUuid(null); // Clear after using
  }, [recorder, timer, preRecordingSessionUuid, patient]);

  // Pause recording
  const pauseRecording = useCallback(async () => {
    recorder.pause();
    timer.pause();
    setShowRecordingModal(true);
  }, [recorder, timer]);

  // Resume recording
  const resumeRecording = useCallback(async () => {
    recorder.resume();
    timer.start();
    setShowRecordingModal(false);
  }, [recorder, timer]);

  // Expand modal from bar (patient/notes/pause)
  const expandModal = useCallback(() => {
    setShowRecordingModal(true);
    recorder.pause();
    timer.pause();
  }, [recorder, timer]);

  // End recording (cancel)
  const endRecording = useCallback(async () => {
    await recorder.end();
    setShowRecordingModal(false);
    timer.reset();
    resetUserNotes();
    setPreRecordingSessionUuid(null); // Clear external session UUID
    resetAttachedDocuments(); // Clear attached documents
  }, [recorder, timer, resetUserNotes, resetAttachedDocuments]);

  // Update patient state when selected in RecordingModal
  const updatePatient = useCallback((newPatient) => {
    setPatient(newPatient);
  }, []);

  // Open new visit (higher-level API)
  const openNewVisit = useCallback((newPatient = null, fromDetails = false) => {
    setPatient(newPatient);
    setFromPatientDetails(fromDetails);
    setShowRecordingModal(true);
    resetUserNotes();
    setPreRecordingSessionUuid(null); // Clear external session UUID
    resetAttachedDocuments(); // Clear attached documents
  }, [resetUserNotes, resetAttachedDocuments]);

  // Pause only (no modal)
  const pauseOnly = useCallback(() => {
    recorder.pause();
    timer.pause();
  }, [recorder, timer]);

  // Set session UUID externally (for document uploads before recording)
  const setSessionUuid = useCallback((uuid) => {
    setPreRecordingSessionUuid(uuid);
  }, []);

  const hasActiveSession = recorder.isRecording || recorder.isPaused;
  // Use pre-recording session UUID if available, otherwise use recorder's session UUID
  const sessionUuid = preRecordingSessionUuid || recorder.sessionUuid;

  // Memoize the context value to prevent re-renders
  const contextValue = useMemo(() => ({
    // UI state
    showRecordingModal,
    setShowRecordingModal,
    showRecordingBar,
    // Recorder state and controls
    isRecording: recorder.isRecording,
    isPaused: recorder.isPaused,
    startRecording,
    pauseRecording,
    pauseOnly,
    resumeRecording,
    endRecording,
    sessionUuid,
    totalChunks: recorder.totalChunks,
    problemType: recorder.problemType,
    problemStats: recorder.problemStats,
    elapsedTime: timer.elapsedTime,
    // Microphone selection
    audioDevices: recorder.audioDevices,
    selectedAudioDevice: recorder.selectedAudioDevice,
    changeAudioDevice: recorder.changeAudioDevice,
    // Modal/Bar controls
    expandModal,
    // Patient
    patient,
    setPatient: updatePatient,
    fromPatientDetails,
    openNewVisit,
    // Notes
    userNotes,
    setUserNotes,
    resetUserNotes,
    hasActiveSession,
    // Session UUID setters
    setSessionUuid,
    // Documents
    attachedDocuments,
    setAttachedDocuments,
    resetAttachedDocuments
  }), [
    showRecordingModal,
    showRecordingBar,
    recorder,
    timer.elapsedTime,
    startRecording,
    pauseRecording,
    pauseOnly,
    resumeRecording,
    endRecording,
    sessionUuid,
    expandModal,
    patient,
    updatePatient,
    fromPatientDetails,
    openNewVisit,
    userNotes,
    setUserNotes,
    resetUserNotes,
    hasActiveSession,
    setSessionUuid,
    attachedDocuments,
    setAttachedDocuments,
    resetAttachedDocuments,
  ]);

  return (
    <RecordingContext.Provider value={contextValue}>
      {children}
    </RecordingContext.Provider>
  );
}

export default RecordingContext;
